import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, OnChanges, Output, signal, inject, ViewChild } from '@angular/core';
import { Pause } from 'lucide-angular';
import { Tooltip } from 'primeng/tooltip';
import { ButtonComponent } from 'src/app/@core/components/ui/button/button.component';
import { IconComponent } from 'src/app/@core/components/ui/icon/icon.component';
import { THREAD_METADATA_STATUS } from '../../../models/model';
import { ThreadMessagesService } from '../../../services/thread-messages.service';
import { CreditsOverviewComponent } from 'src/app/features/credits/components/credits-overview/credits-overview.component';
import { AuthenticationService } from 'src/app/@core/services/authentication.service';
import { PromptEnhancementCardComponent } from '../prompt-enhancement-card/prompt-enhancement-card.component';
import { PromptReuseCardComponent } from '../prompt-reuse-card/prompt-reuse-card.component';
import {
    AUTO_APPROVE_SECONDS,
    clearCountdown,
    formatCountdown,
    readCountdown,
    writeCountdown,
} from '../../../utils/interrupt-countdown';

@Component({
    selector: 'app-thread-state-indicator',
    standalone: true,
    imports: [CommonModule, IconComponent, ButtonComponent, CreditsOverviewComponent, Tooltip, PromptEnhancementCardComponent, PromptReuseCardComponent],
    templateUrl: './thread-state-indicator.component.html',
    styleUrl: './thread-state-indicator.component.scss'
})
export class ThreadStateIndicatorComponent implements OnInit, OnDestroy, OnChanges {
    @Input() startTimer: boolean = false;
    @Input() pauseTimer: boolean = false;
    /** When false (view-only shared user), action CTAs are hidden and a notice is shown instead. */
    @Input() canEdit: boolean = true;
    @Output() approvePlan = new EventEmitter<void>();
    @Output() rejectPlan = new EventEmitter<void>();
    /** Emitted with the edited Markdown string when the user modifies and approves the enhanced prompt. */
    @Output() editPlan = new EventEmitter<string>();
    /** Emitted with freeform feedback text when the user asks the AI to revise the enhanced prompt. */
    @Output() revisePlan = new EventEmitter<string>();
    @Output() timerExpired = new EventEmitter<void>();
    @Output() savePrompt = new EventEmitter<string>();
    @Output() continueAfterError = new EventEmitter<void>();
    @Output() continueAfterPause = new EventEmitter<void>();
    /** Emitted with the promptId the user chose to reuse. */
    @Output() useExistingPrompt = new EventEmitter<string>();
    /** Emitted when the user wants a freshly-generated enhancement instead. */
    @Output() generateNewPrompt = new EventEmitter<void>();

    public threadMessagesService = inject(ThreadMessagesService);
    private authService = inject(AuthenticationService);
    @ViewChild('creditsOverviewRef') creditsOverviewRef!: CreditsOverviewComponent;

    get askModeEnabled(): boolean {
        return this.authService.getUser()?.agentModeSettings?.askMode ?? true;
    }

    get agentModeEnabled(): boolean {
        return this.authService.getUser()?.agentModeSettings?.agentMode ?? true;
    }

    get canRequestCredits(): boolean {
        return this.askModeEnabled || this.agentModeEnabled;
    }

    // Timer properties
    timeRemaining = signal<number>(AUTO_APPROVE_SECONDS);
    private timerInterval?: number;
    private isTimerRunning = false;
    Pause = Pause;

    /** Thread the countdown deadline is persisted against. */
    private get threadId(): string {
        return this.threadMessagesService.activeThreadId();
    }

    THREAD_METADATA_STATUS = THREAD_METADATA_STATUS;

    ngOnInit(): void {
        if (this.startTimer && this.canEdit) {
            this.startTimerIfInterrupted();
        }
    }

    ngOnDestroy(): void {
        this.clearTimer();
    }

    ngOnChanges(): void {
        if (this.startTimer) {
            if (this.threadMessagesService.threadMetadataStatus() === THREAD_METADATA_STATUS.INTERRUPTED) {
                this.startTimerIfInterrupted();
            } else {
                // No longer awaiting the user — drop the persisted deadline so the
                // next interrupt on this thread starts from a full countdown.
                this.clearTimer();
                clearCountdown(this.threadId);
            }
        } else {
            this.clearTimer();
        }
    }

    /**
     * True when an interrupt card is on screen. Each card runs its own countdown
     * off the same stored deadline, so the parent timer must stand down or both
     * would expire on the same tick and fire approve twice.
     */
    private cardOwnsTimer(): boolean {
        return !!this.threadMessagesService.promptReuseSuggestion()
            || !!this.threadMessagesService.promptEnhancement();
    }

    startTimerIfInterrupted(): void {
        if (this.cardOwnsTimer()) return;
        if (this.threadMessagesService.threadMetadataStatus() === THREAD_METADATA_STATUS.INTERRUPTED && !this.isTimerRunning) {
            // Resume the deadline this thread was already counting down to, so a
            // browser refresh doesn't hand the user a fresh two minutes.
            const restored = readCountdown(this.threadId);
            if (restored === 0) {
                this.onTimerExpired();
                return;
            }

            const seconds = restored ?? AUTO_APPROVE_SECONDS;
            this.timeRemaining.set(seconds);
            writeCountdown(this.threadId, seconds);

            this.isTimerRunning = true;
            this.timerInterval = window.setInterval(() => {
                // A card can mount after this timer started (on reload the interrupt
                // payload arrives asynchronously). Hand ownership over when it does,
                // leaving the stored deadline alone for the card to pick up.
                if (this.cardOwnsTimer()) {
                    this.clearTimer();
                    return;
                }
                const currentTime = this.timeRemaining();
                if (currentTime > 0) {
                    this.timeRemaining.set(currentTime - 1);
                } else {
                    this.onTimerExpired();
                }
            }, 1000);
        }
    }

    private clearTimer(): void {
        if (this.timerInterval) {
            clearInterval(this.timerInterval);
            this.timerInterval = undefined;
            this.isTimerRunning = false;
        }
    }

    /**
     * Stop the countdown and forget its deadline — the interrupt is resolved.
     * Deliberately not called from ``ngOnDestroy``: a refresh tears the view
     * down, and that is precisely when the deadline must survive.
     */
    private endTimer(): void {
        this.clearTimer();
        clearCountdown(this.threadId);
    }

    private onTimerExpired(): void {
        this.endTimer();
        this.timerExpired.emit();
    }

    onApprovePlan(): void {
        this.endTimer();
        this.approvePlan.emit();
    }

    onRejectPlan(): void {
        this.endTimer();
        this.rejectPlan.emit();
    }

    onContinueAfterError(): void {
        this.continueAfterError.emit();
    }

    onContinueAfterPause(): void {
        this.continueAfterPause.emit();
    }

    onEditPlan(edited: string): void {
        this.editPlan.emit(edited);
    }

    onRevisePlan(feedback: string): void {
        this.revisePlan.emit(feedback);
    }

    onSavePrompt(content: string): void {
        this.savePrompt.emit(content);
    }

    formatTime(seconds: number): string {
        return formatCountdown(seconds);
    }

    // ── Prompt-reuse card handlers ────────────────────────────────────────────

    onUseExistingPrompt(promptId: string): void {
        this.useExistingPrompt.emit(promptId);
    }

    onGenerateNewPrompt(): void {
        this.generateNewPrompt.emit();
    }
}
